<script setup>
// ============================================================================
// BonBoZ: trang Thống kê > TƯƠNG TÁC — dựng theo đúng bố cục Pancake.
//
// BỐ CỤC:
//   1. Tương Tác khách hàng — vòng nửa, chia khách mới / khách cũ, kèm hai ô
//      "TT qua tin nhắn" và "TT qua bình luận"
//   2. Đơn hàng             — cùng khuôn vòng nửa, kèm hai ô tỉ lệ chốt đơn
//   3. Thống kê chi tiết    — bảng đầu hai tầng, tab [Theo thời gian]
//      [Theo nhân viên] + [Theo ngày][Theo giờ] + Tuỳ chỉnh
//
// MỘT "LƯỢT TƯƠNG TÁC" LÀ MỘT HỘI THOẠI ĐƯỢC CHẠM VÀO, không phải một tin
// nhắn — đây là chỗ dễ đọc nhầm nhất giữa trang này và trang Nhân viên. Lý do
// đầy đủ ở đầu `Bonboz::TuongTacBuilder`.
//
// NÚT [POS] để TẮT chứ không bịa số: Pancake tách đơn theo hai nguồn (Pancake
// và Pancake POS), ta chưa nối POS nào. Bỏ hẳn nút thì người quen Pancake đi
// tìm; để nút bấm được mà trả 0 thì tệ hơn — nó nói dối.
// ============================================================================
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import HieuSuatAPI from 'dashboard/api/bonboz/hieuSuat';
import { useAlert } from 'dashboard/composables';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import { kyTruoc } from 'dashboard/helper/tongQuan';
import KhoiVongNua from './KhoiVongNua.vue';
import BangTuongTac from './BangTuongTac.vue';
import ReportHeader from '../components/ReportHeader.vue';
import OverviewReportFilters from '../components/OverviewReportFilters.vue';
import PhamVi from './PhamVi.vue';

const { t } = useI18n();
// Moi con so tren trang nay chi duoc dem tren nhung trang dang chon o bang
// dieu khien.
const { selectedInboxIds } = useSelectedPages();

const dangTai = ref(true);
const dangTaiBang = ref(false);
const khoang = ref(null);
const gom = ref('thoi_gian');
const gomGio = ref('ngay');

// Hai khối trên đầu luôn đọc từ lượt gọi theo THỜI GIAN — chúng không đổi theo
// tab của bảng, nên tải lại khi bấm tab là tải thừa.
const soLieu = ref(null);
const soLieuTruoc = ref(null);
const bangRieng = ref(null); // nguồn của bảng khi khác tab mặc định

const goi = (tu, den, kieu, gio) =>
  HieuSuatAPI.tuongTac({
    since: tu,
    until: den,
    inboxIds: selectedInboxIds.value,
    gom: kieu,
    gomGio: gio,
  }).then(r => r.data);

const taiBang = async () => {
  // Tab mặc định (thời gian + ngày) dùng chính dữ liệu đã tải cho hai khối trên.
  if (gom.value === 'thoi_gian' && gomGio.value === 'ngay') {
    bangRieng.value = null;
    return;
  }
  dangTaiBang.value = true;
  try {
    bangRieng.value = await goi(
      khoang.value.from,
      khoang.value.to,
      gom.value,
      gomGio.value
    );
  } catch {
    bangRieng.value = null;
    gom.value = 'thoi_gian';
    gomGio.value = 'ngay';
    useAlert(t('BONBOZ.TUONG_TAC.LOI_TAI'));
  } finally {
    dangTaiBang.value = false;
  }
};

const tai = async () => {
  if (!khoang.value) return;
  const { from, to } = khoang.value;
  const ky = kyTruoc(from, to);
  dangTai.value = true;
  try {
    // Kỳ trước chạy song song: không ai phải chờ nó xong mới thấy số kỳ này.
    const [nay, cu] = await Promise.all([
      goi(from, to, 'thoi_gian', 'ngay'),
      ky ? goi(ky.tu, ky.den, 'thoi_gian', 'ngay') : Promise.resolve(null),
    ]);
    soLieu.value = nay;
    soLieuTruoc.value = cu;
    await taiBang();
  } catch {
    soLieu.value = null;
    soLieuTruoc.value = null;
    useAlert(t('BONBOZ.TUONG_TAC.LOI_TAI'));
  } finally {
    dangTai.value = false;
  }
};

const doiBoLoc = ({ from, to }) => {
  if (!from || !to) return;
  khoang.value = { from, to };
  tai();
};

// Lua chon trang nam trong `ui_settings` cua nguoi dung, tai ve SAU khi trang
// da mount — lan tai dau tien co the chay khi mang con rong (= tat ca).
watch(selectedInboxIds, tai);

const doiGom = async kieu => {
  gom.value = kieu;
  // Tab nhân viên không có khái niệm ngày/giờ; đưa về mặc định để lúc quay lại
  // tab thời gian không thấy một lựa chọn mình chưa từng bấm.
  if (kieu === 'nhan_vien') gomGio.value = 'ngay';
  await taiBang();
};

const doiGomGio = async kieu => {
  gomGio.value = kieu;
  await taiBang();
};

// ------------------------------------------------------------------ số liệu
const tong = computed(() => soLieu.value?.tong || null);
const tongTruoc = computed(() => soLieuTruoc.value?.tong || null);

// Chênh lệch phần trăm. `null` khi kỳ trước bằng 0: chia cho 0 ra Infinity, mà
// "tăng vô hạn" thì không phải một con số ai dùng được.
const lech = khoa => {
  const truoc = tongTruoc.value?.[khoa] ?? 0;
  if (!truoc) return null;
  return Math.round((((tong.value?.[khoa] ?? 0) - truoc) / truoc) * 100);
};

// Tỉ lệ chốt đơn: mỗi cái chia trong đúng rổ của nó — đơn của khách mới chia
// cho tương tác với khách mới, không phải cho tổng.
const tiLe = (tu, mau) => {
  const duoi = tong.value?.[mau] ?? 0;
  if (!duoi) return '0%';
  return `${Math.round(((tong.value[tu] ?? 0) / duoi) * 1000) / 10}%`;
};

const khoiTuongTac = computed(() => ({
  tong: (tong.value?.tong_tt ?? 0).toLocaleString(),
  lech: lech('tong_tt'),
  phan: [
    {
      khoa: 'moi',
      nhan: t('BONBOZ.TUONG_TAC.TT_KHACH_MOI'),
      so: tong.value?.tt_moi ?? 0,
      bien: '--bonboz-xong',
    },
    {
      khoa: 'cu',
      nhan: t('BONBOZ.TUONG_TAC.TT_KHACH_CU'),
      so: tong.value?.tt_cu ?? 0,
      bien: '--bonboz-nguoi',
    },
  ],
  oDuoi: [
    {
      khoa: 'tin',
      nhan: t('BONBOZ.TUONG_TAC.TT_QUA_TIN'),
      giaTri: (tong.value?.tin_nhan ?? 0).toLocaleString(),
      lech: lech('tin_nhan'),
      icon: 'i-lucide-mail',
      bien: '--bonboz-mo',
    },
    {
      khoa: 'bl',
      nhan: t('BONBOZ.TUONG_TAC.TT_QUA_BINH_LUAN'),
      giaTri: (tong.value?.binh_luan ?? 0).toLocaleString(),
      lech: lech('binh_luan'),
      icon: 'i-lucide-message-circle',
      bien: '--bonboz-cho',
      gt: t('BONBOZ.TUONG_TAC.GT_BINH_LUAN'),
    },
  ],
}));

const khoiDonHang = computed(() => ({
  tong: (tong.value?.tong_dh ?? 0).toLocaleString(),
  lech: lech('tong_dh'),
  phan: [
    {
      khoa: 'moi',
      nhan: t('BONBOZ.TUONG_TAC.DH_KHACH_MOI'),
      so: tong.value?.dh_moi ?? 0,
      bien: '--bonboz-mo',
    },
    {
      khoa: 'cu',
      nhan: t('BONBOZ.TUONG_TAC.DH_KHACH_CU'),
      so: tong.value?.dh_cu ?? 0,
      bien: '--bonboz-nguoi',
    },
  ],
  oDuoi: [
    {
      khoa: 'cd_moi',
      nhan: t('BONBOZ.TUONG_TAC.TI_LE_CD_MOI'),
      giaTri: tiLe('dh_moi', 'tt_moi'),
      lech: null,
      icon: 'i-lucide-shopping-basket',
      bien: '--bonboz-xong',
    },
    {
      khoa: 'cd_cu',
      nhan: t('BONBOZ.TUONG_TAC.TI_LE_CD_CU'),
      giaTri: tiLe('dh_cu', 'tt_cu'),
      lech: null,
      icon: 'i-lucide-shopping-bag',
      bien: '--bonboz-tre',
    },
  ],
}));

const nguonBang = computed(() => bangRieng.value || soLieu.value);
</script>

<template>
  <ReportHeader
    :header-title="$t('BONBOZ.TUONG_TAC.TIEU_DE')"
    :header-description="$t('BONBOZ.TUONG_TAC.MO_TA')"
  />

  <div class="flex flex-col w-full gap-4 mx-auto max-w-6xl">
    <OverviewReportFilters :disabled="dangTai" @filter-change="doiBoLoc" />

    <PhamVi />

    <div class="grid gap-4 lg:grid-cols-2 lg:items-start">
      <KhoiVongNua
        :tieu-de="$t('BONBOZ.TUONG_TAC.KHOI_TT')"
        :mo-ta="$t('BONBOZ.TUONG_TAC.KHOI_TT_MO_TA')"
        :nhan-giua="$t('BONBOZ.TUONG_TAC.TONG_LUOT')"
        :tong="khoiTuongTac.tong"
        :lech="khoiTuongTac.lech"
        :phan="khoiTuongTac.phan"
        :o-duoi="khoiTuongTac.oDuoi"
        :dang-tai="dangTai"
      />

      <KhoiVongNua
        :tieu-de="$t('BONBOZ.TUONG_TAC.KHOI_DH')"
        :mo-ta="$t('BONBOZ.TUONG_TAC.KHOI_DH_MO_TA')"
        :nhan-giua="$t('BONBOZ.TUONG_TAC.TONG_DON')"
        :tong="khoiDonHang.tong"
        :lech="khoiDonHang.lech"
        :phan="khoiDonHang.phan"
        :o-duoi="khoiDonHang.oDuoi"
        :dang-tai="dangTai"
      >
        <!-- Hai nguồn đơn của Pancake. Nút POS TẮT vì chưa nối POS nào — để nó
             bấm được mà trả 0 thì nó đang nói dối. -->
        <template #goc-phai>
          <div class="flex p-0.5 rounded-lg shrink-0 bg-n-alpha-1">
            <span
              class="px-3 py-1 text-sm font-medium rounded-md bg-n-brand text-white"
            >
              {{ $t('BONBOZ.TUONG_TAC.NGUON_CHAT') }}
            </span>
            <span
              v-tooltip.top="$t('BONBOZ.TUONG_TAC.GT_POS')"
              class="px-3 py-1 text-sm rounded-md cursor-not-allowed text-n-slate-10"
            >
              {{ $t('BONBOZ.TUONG_TAC.NGUON_POS') }}
            </span>
          </div>
        </template>
      </KhoiVongNua>
    </div>

    <BangTuongTac
      :dong="nguonBang?.dong || []"
      :tong="nguonBang?.tong || null"
      :gom="gom"
      :gom-gio="gomGio"
      :dang-tai="dangTai || dangTaiBang"
      @doi-gom="doiGom"
      @doi-gom-gio="doiGomGio"
    />
  </div>
</template>
